import { useState, useEffect } from 'react';
import { API_BASE } from '../../utils/config';

const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'BU_HEAD',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

let exitsCache = null;
let exitsCacheTimestamp = 0;
const CACHE_TTL_MS = 15000;

export default function BUExitApprovals({ onAction }) {
  const [exits, setExits] = useState(exitsCache || []);
  const [loading, setLoading] = useState(!exitsCache);
  const [submittingId, setSubmittingId] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);

  const fetchExits = async (force = false) => {
    const now = Date.now();
    if (!force && exitsCache && (now - exitsCacheTimestamp < CACHE_TTL_MS)) {
      setExits(exitsCache);
      setLoading(false);
      return;
    }

    try {
      if (!exitsCache) setLoading(true);
      const data = await apiCall('/api/employees/exits/pending');
      exitsCache = data;
      exitsCacheTimestamp = Date.now();
      setExits(data);
    } catch (err) {
      console.error('Failed to fetch pending exits:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExits();
  }, []);

  const handleApprove = async (id) => {
    try {
      setSubmittingId(id);
      await apiCall(`/api/employees/exits/${id}/approve`, {
        method: 'POST'
      });
      exitsCache = null;
      fetchExits(true);
      if (onAction) onAction();
    } catch (err) {
      console.error('Failed to approve exit request:', err);
      alert('Failed to approve employee exit.');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleNotify = async (id) => {
    try {
      setNotifyingId(id);
      const res = await apiCall(`/api/employees/exits/${id}/notify`, {
        method: 'POST'
      });
      alert(res.message || 'Notification sent successfully!');
    } catch (err) {
      console.error('Failed to send exit notification:', err);
      alert('Failed to send exit notification.');
    } finally {
      setNotifyingId(null);
    }
  };

  return (
    <div style={styles.tableCard}>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h4 style={styles.title}>Pending Exit Clearances & Approvals</h4>
            <p style={styles.subtitle}>Review final working days for exiting employees and sign off exit clearances.</p>
          </div>
          {exits.length > 0 && (
            <span style={styles.badgePendingCount}>
              ⚡ {exits.length} Action Required
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div style={styles.loader}>Loading exit logs...</div>
      ) : exits.length === 0 ? (
        <div style={styles.empty}>No employee exits pending approval.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.trHeader}>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Project</th>
                <th style={styles.th}>Last Working Day</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exits.map(exit => (
                <tr key={exit.id} style={styles.tr}>
                  <td style={styles.tdTitle}>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--text-main, #0f172a)' }}>{exit.full_name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)' }}>{exit.email}</div>
                    </div>
                  </td>
                  <td style={styles.tdProject}>{exit.project_name || 'Unassigned'}</td>
                  <td style={styles.tdDate}>
                    {exit.last_working_day ? new Date(exit.last_working_day).toLocaleDateString() : 'N/A'}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.badgePending}>
                      {exit.exit_status === 'EXIT_APPROVED' ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td style={styles.tdAction}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button 
                        style={styles.btnApprove} 
                        onClick={() => handleApprove(exit.id)}
                        disabled={submittingId === exit.id}
                      >
                        {submittingId === exit.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button 
                        style={styles.btnNotify} 
                        onClick={() => handleNotify(exit.id)}
                        disabled={notifyingId === exit.id}
                      >
                        {notifyingId === exit.id ? 'Sending...' : 'Notify'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    boxSizing: 'border-box',
    width: '100%'
  },
  cardHeader: {
    marginBottom: '20px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    paddingBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  subtitle: {
    margin: '4px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '13px'
  },
  badgePendingCount: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#d97706',
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    border: '1px solid rgba(245, 158, 11, 0.25)',
    padding: '4px 12px',
    borderRadius: '99px'
  },
  loader: {
    textAlign: 'center',
    padding: '24px',
    color: 'var(--text-muted, #64748b)'
  },
  empty: {
    textAlign: 'center',
    padding: '36px',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdTitle: {
    padding: '14px 16px',
    fontWeight: '600',
    color: 'var(--text-main, #0f172a)'
  },
  tdProject: {
    padding: '14px 16px',
    fontWeight: '600',
    color: 'var(--accent-primary, #4f46e5)'
  },
  tdDate: {
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: '500'
  },
  td: {
    padding: '14px 16px',
    fontSize: '13px'
  },
  tdAction: {
    padding: '14px 16px',
    textAlign: 'right'
  },
  btnApprove: {
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '12px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
    transition: 'all 0.2s'
  },
  btnNotify: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '12px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
    transition: 'all 0.2s'
  },
  badgePending: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '3px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-block'
  }
};
